import * as React from 'react';
import { API, endpoints } from 'src/api';
import { useSocketSyncUser } from 'src/api';
import { useSettings } from 'src/api/hooks/fetch-settings';
import Analytics from 'src/api/services/analytics/analytics';
import { useAuth } from 'src/shared/hooks/use-auth';

import { useLoaderStore } from '../loaders';
import { Settings as SettingsComponent } from './settings';
import { SettingsProvider, SettingsProviderProps } from './store';

const { useCallback, useEffect } = React;

export const Settings = ({
  isOpen,
  onClose,
  currentMenu,
}: {
  isOpen: boolean;
  currentMenu?: string;
  onClose: () => void;
}): JSX.Element => {
  const { settingsData, revalidate, isLoading } = useSettings();
  const { setIsLoading } = useLoaderStore();
  const fetchUserDetails = useAuth((s) => s.fetchUserDetails);

  const handleOnSaveProfile: SettingsProviderProps['onSaveProfile'] = useCallback(
    async ({ name, lastName, info, profileImage }) => {
      const data = new FormData();
      data.append('firstname', name);
      data.append('lastname', lastName);
      data.append('info', JSON.stringify(info));
      data.append('image', profileImage);

      const response = await API.post(endpoints.settings.profile.url, data, {
        headers: { ['Content-Type']: 'multipart/form-data' },
      });

      if (response?.data?.success) {
        await revalidate();
        fetchUserDetails();
        // location.reload();
        return { isSuccess: true };
      }
    },
    [revalidate]
  );

  const handleChangePassword: SettingsProviderProps['onChangePassword'] = async ({
    newPassword,
  }) => {
    const response = await API.put(endpoints.settings.changePassword.url, {
      new_password: newPassword,
    });
    if (response.data.success) {
      return { isSuccess: true };
    } else if (!response.data.success && response.data.message === 'Incorrect Password!') {
      return { wrongPassword: true, isSuccess: false };
    }
    return { isSuccess: false };
  };

  const handleChangeEmail: SettingsProviderProps['onChangeEmail'] = async ({ email }) => {
    const response = await API.put(endpoints.settings.changeEmail.url, {
      email,
    });
    if (response.data.success) {
      return { isSuccess: true };
    }
    return { isSuccess: false };
  };

  const handleDeleteAccount: SettingsProviderProps['onDeleteAccount'] = async ({ challenge }) => {
    const response = await API.post(endpoints.settings.deleteAccount.url, {
      challenge,
    });

    if (response.data.success) {
      Analytics.track('Delete Account');
      return { isSuccess: true };
    }
    return { isSuccess: false };
  };

  const handleUpdatePreferences = async (values) => {
    const response = await API.put(endpoints.settings.preferences.url, {
      ...values,
    });
    if (response.data.success) {
      await revalidate();
      return { isSuccess: true };
    }
  };

  const handleUpdateAvailability = async (values) => {
    const response = await API.put(endpoints.settings.availability.url, {
      ...values,
    });
    if (response.data.success) {
      await revalidate();
      return { isSuccess: true };
    }
  };

  const handleInviteUsers = async (values) => {
    const response = await API.post(endpoints.inviteUsers.url, values);

    if (response.data.success) {
      revalidate();
      fetchUserDetails();
      Analytics.track('Member Add');
      return { isSuccess: true };
    }
  };

  const handleRemoveUser = async (values) => {
    const response = await API.delete(`settings/member?id=${values}`);

    if (response.data.success) {
      revalidate();
      fetchUserDetails();
      return { isSuccess: true };
    }
  };
  const handleConvertUser = async (values) => {
    const response = await API.post(`settings/guest/convert`, { id: values });

    if (response.data.success) {
      revalidate();
      fetchUserDetails();
      return { isSuccess: true };
    }
  };

  const handleUpdateMember = async (values) => {
    const response = await API.put(endpoints.settings.updateMember.url, values);

    if (response.data.success) {
      await revalidate();
      fetchUserDetails();
      return { isSuccess: true };
    }
  };

  const handleUpdateOrganisation = async (values) => {
    const response = await API.put(endpoints.settings.updateOrganisation.url, values);

    if (response.status === 200) {
      await revalidate();
      fetchUserDetails();
      return { isSuccess: true };
    }
  };
  const handleDeleteOrganisation = async (values) => {
    const response = await API.post('settings/organisation/delete', values);

    if (response.data.success) {
      await revalidate();
      fetchUserDetails();
      return { isSuccess: true };
    }
  };
  const handleUpdateOrganizationImage = async (image) => {
    const data = new FormData();
    data.append('image', image);

    const response = await API.put(endpoints.settings.updateOrganisationImage.url, data, {
      headers: { ['Content-Type']: 'multipart/form-data' },
    });

    if (response.data.success) {
      revalidate();
      fetchUserDetails();
      return { isSuccess: true };
    }
  };
  const handleUpdateOrganizationLogo = async (logo) => {
    let response;

    if (logo === 'remove_logo') {
      response = await API.post('/settings/logo', { remove: true });
    } else if (logo instanceof File) {
      const data = new FormData();
      data.append('logo', logo);
      response = await API.post('/settings/logo', data, {
        headers: { ['Content-Type']: 'multipart/form-data' },
      });
    }
    if (response.data.success) {
      revalidate();
      fetchUserDetails();
      return { isSuccess: true };
    }
  };
  useEffect(() => {
    isLoading ? setIsLoading(true) : setIsLoading(false);
  }, [isLoading]);

  const authData = useAuth((s) => s.userDetails);
  const socketSyncUser = useSocketSyncUser({ user: authData?.userId });

  useEffect(() => {
    revalidate();
  }, [socketSyncUser]);

  return (
    <SettingsProvider
      onChangeEmail={handleChangeEmail}
      onDeleteAccount={handleDeleteAccount}
      onSaveProfile={handleOnSaveProfile}
      onChangePassword={handleChangePassword}
      onUpdatePreferences={handleUpdatePreferences}
      onUpdateAvailability={handleUpdateAvailability}
      onInviteUsers={handleInviteUsers}
      onRemoveUser={handleRemoveUser}
      onConvertUser={handleConvertUser}
      onUpdateOrganisationName={handleUpdateOrganisation}
      onUpdateMemberAccess={handleUpdateMember}
      onDeleteOrganisation={handleDeleteOrganisation}
      onUpdateOrganisationImage={handleUpdateOrganizationImage}
      onUpdateOrganisationLogo={handleUpdateOrganizationLogo}
    >
      <SettingsComponent
        companyName="Sketchnote"
        workspaces={[]}
        open={isOpen}
        currentMenu={currentMenu}
        onClose={onClose}
        data={settingsData}
      />
    </SettingsProvider>
  );
};
